<template>
  <div
    id="shipping-address"
    role="region"
    aria-labelledby="shipping-address-heading"
    class="grid-x large-padding-top-m large-padding-bottom-m padding-top-s padding-bottom-s border-top"
  >
    <div class="small-7 medium-3 medium-padding-right-xxxl">
      <h3 v-text="config.SHIPPING_ADDRESS_SECTION.title"></h3>
    </div>
    <div v-if="returnItemStore.returnItemsCurrentStep >= 3" class="small-5 medium-2 align-top padding-top-xxs text-right medium-text-left">
      <button
        v-if="!returnItemStore.returnItemsEditable.shippingAddress && returnItemStore.returnSelections.shippingAddress !== ''"
        class="link-body"
        aria-label="Change shipping address"
        @click="onChange"
      >
        Change
      </button>
    </div>
    <div v-if="returnItemStore.returnItemsCurrentStep >= 3" class="small-12 medium-7 small-margin-top-s medium-margin-top-z large-padding-left-m">
      <div v-if="returnItemStore.returnItemsEditable.shippingAddress">
        <CardRadio
          v-slot="{ option }"
          v-model="shippingAddressChoice"
          value-key="addressId"
          :options="visibleShippingAddresses"
          content-class="grid-x align-middle"
          :show-radio="shippingAddressLists.length > 1"
          @update:model-value="handleShippingAddressChoice"
        >
          <div class="cell auto" :aria-checked="shippingAddressChoice === option.addressId" tabindex="0" role="radio">
            <p class="margin-bottom-xxs">
              {{ `${option.firstName} ${option.lastName}` }}
            </p>
            <p v-text="commaSeparate(option.addressLine1, option.addressLine2)"></p>
            <p v-text="commaSeparate(option.city, option.state, option.postalCode)"></p>
            <p v-if="option.defaultAddress" class="grid-x align-middle margin-top-xs" aria-hidden="true">
              <span class="confirmation-checkmark"></span> {{ config.SHIPPING_ADDRESS_SECTION.default }}
            </p>
          </div>
          <button
            v-if="shippingAddressLists.length > 1 && visibleShippingAddresses.length === 1"
            id="edit-btn"
            class="link-body align-self-middle"
            @click="onEdit"
          >
            Edit
          </button>
        </CardRadio>

        <AppButton type="button" class="primary expanded continue" :disabled="!shippingAddressChoice" @click="onContinue">
          {{ change ? 'Save' : 'Continue' }}
        </AppButton>
      </div>
      <div v-else-if="selectedShippingAddressDetails" class="section-border">
        <p class="margin-bottom-xxs" v-text="`${selectedShippingAddressDetails.firstName} ${selectedShippingAddressDetails.lastName}`"></p>
        <p v-text="commaSeparate(selectedShippingAddressDetails.addressLine1, selectedShippingAddressDetails.addressLine2)"></p>
        <p
          v-text="commaSeparate(selectedShippingAddressDetails.city, selectedShippingAddressDetails.state, selectedShippingAddressDetails.postalCode)"
        ></p>
        <p v-if="selectedShippingAddressDetails.defaultAddress" class="grid-x align-middle margin-top-xs">
          <span class="confirmation-checkmark"></span> {{ config.SHIPPING_ADDRESS_SECTION.default }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
//imports
import { ref, computed, onMounted, watch } from 'vue';
import { fireTag } from '@vue-util/tagManager';
import CardRadio from '@/components/CardRadio.vue';
import AppButton from '@/components/AppButton.vue';
import { commaSeparate } from '@/utils/General';
import { useReturnItemStore } from '@/stores/returns/useReturnItemStore';

//config
import * as config from 'config';

//initialize store
const returnItemStore = useReturnItemStore();

//local variable
const shippingAddressChoice = ref('');
const change = ref(false);
const showAllAddresses = ref(false);

//computed properties

// returns shipping addresses and default address always be on top
const shippingAddressLists = computed(() =>
  returnItemStore.returnItems.shippingAddresses.reduce((acc, { address, ...rest }) => {
    const flatAddressObj = { ...rest, ...address };
    if (flatAddressObj.defaultAddress) {
      acc.unshift(flatAddressObj);
    } else {
      acc.push(flatAddressObj);
    }
    return acc;
  }, []),
);
const visibleShippingAddresses = computed(() =>
  showAllAddresses.value ? shippingAddressLists.value : shippingAddressLists.value.filter((val) => val.defaultAddress),
);
const selectedShippingAddressDetails = computed(() => returnItemStore.returnSelections?.shippingAddressDetails);

const handleShippingAddressChoice = (event) => {
  shippingAddressChoice.value = event;
};

const onChange = () => {
  change.value = true;
  showAllAddresses.value = true;
  if (!returnItemStore.returnSelections.returnMethod) {
    returnItemStore.SET_RETURN_ITEMS_CURRENT_STEP(3);
  }
  returnItemStore.SET_RETURN_EDITABLE({
    returnItems: false,
    refundOptions: false,
    shippingAddress: true,
    returnMethod: false,
  });
};

const onContinue = () => {
  const shippingAddressDetails = visibleShippingAddresses.value.find((add) => add.addressId === shippingAddressChoice.value);
  returnItemStore.SET_RETURN_SELECTIONS({ shippingAddressDetails });
  if (!returnItemStore.returnSelections.returnMethod) {
    returnItemStore.SET_RETURN_EDITABLE({
      returnItems: false,
      refundOptions: false,
      shippingAddress: false,
      returnMethod: true,
    });
    returnItemStore.SET_RETURN_ITEMS_CURRENT_STEP(4);
  } else {
    returnItemStore.SET_RETURN_EDITABLE({
      returnItems: false,
      refundOptions: false,
      shippingAddress: false,
      returnMethod: false,
    });
  }

  fireTag('link', {
    event_name: 'delivery address',
    site_action: 'delivery address complete',
    action_location: 'order details',
  });
};

watch(
  () => returnItemStore.returnItemsEditable.shippingAddress,
  (sectionOpened) => {
    if (sectionOpened) {
      fireTag('link', {
        event_name: 'delivery address',
        site_action: 'delivery address start',
        action_location: 'order details',
      });
    }
  },
  { immediate: true },
);

const onEdit = (event) => {
  event?.stopPropagation();
  showAllAddresses.value = true;
};

onMounted(() => {
  if (shippingAddressLists.value.length === 1) {
    shippingAddressChoice.value = shippingAddressLists.value[0].addressId;
  }
});
</script>
